import { KbRecordItem } from "shared";

interface BreadcrumbItem {
  [key: string]: {
    id: string;
    kb_type: string;
  };
}

/**
 * Generates breadcrumbs by traversing up the folder hierarchy from the current folder
 * @param currentFolderId - The ID of the current folder
 * @param folderMap - Map of all folders keyed by mapping_id
 * @returns Array of breadcrumb items in order from root to current folder
 */
export const generateBreadcrumbs = (
  currentFolderId: string,
  folderMap: Map<string, KbRecordItem>
): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [];
  
  if (!currentFolderId || !folderMap.has(currentFolderId)) {
    return breadcrumbs;
  }

  let currentFolder = folderMap.get(currentFolderId);
  
  // Traverse up the hierarchy by following parent_id
  while (currentFolder) {
    breadcrumbs.unshift({
      [currentFolder.name]: {
        id: currentFolder.mapping_id,
        kb_type: currentFolder.kb_type,
      },
    });

    // Move to parent folder
    if (currentFolder.parent_id) {
      currentFolder = folderMap.get(currentFolder.parent_id);
    } else {
      break;
    }
  }

  return breadcrumbs;
};

/**
 * Alternative implementation using full_path_id if available
 * @param currentFolder - The current folder item
 * @param folderMap - Map of all folders keyed by mapping_id
 * @returns Array of breadcrumb items
 */
export const generateBreadcrumbsFromPath = (
  currentFolder: KbRecordItem,
  folderMap: Map<string, KbRecordItem>
): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [];
  
  if (!currentFolder.full_path_id) {
    return generateBreadcrumbs(currentFolder.mapping_id, folderMap);
  }

  // Split the full_path_id and build breadcrumbs
  const pathIds = currentFolder.full_path_id.split('/').filter(Boolean);
  
  pathIds.forEach(pathId => {
    const folder = folderMap.get(pathId);
    if (folder) {
      breadcrumbs.push({
        [folder.name]: {
          id: folder.mapping_id,
          kb_type: folder.kb_type,
        },
      });
    }
  });

  return breadcrumbs;
};